import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiLayers,
  FiList,
  FiInbox,
  FiFolder,
} from "react-icons/fi";
import {
  StandardInfoAtom,
  ListStandardInfoAtom,
  DetailStandardInfoAtom,
} from "../../../atoms/standardModelAtom";
import {
  tokens,
  PageShell,
  PageWidth,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  Button,
  IconBtn,
  Input,
  SearchInput,
  TableCard,
  TableScroll,
  Table,
  Th,
  Td,
  Tr,
  Checkbox,
  Badge,
  EmptyState,
  SideList,
  SideItem,
} from "../../../components/admin/AdminUI";

/* 마스터/디테일 2열 그리드 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* 패널: 동일 높이로 늘어남 */
const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  @media (max-width: 1024px) {
    min-height: 360px;
  }
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupScroll = styled.div`
  flex: 1;
  min-height: 80px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 4px 0;
`;

const AddArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
  margin-top: 4px;
  border-top: 1px solid ${tokens.color.borderSubtle};
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid ${tokens.color.borderSubtle};
`;

const CellInput = styled(Input)`
  height: 34px;
  font-size: 13px;
`;

export default function StandardInfo() {
  const [codeGroupInput, setCodeGroupInput] = useRecoilState(StandardInfoAtom);
  const [selectedCodeGroup, setSelectedCodeGroup] = useRecoilState(ListStandardInfoAtom);
  const [selectedCodeGroupDetails, setSelectedCodeGroupDetails] = useRecoilState(DetailStandardInfoAtom);

  const [filterValue, setFilterValue] = useState("");
  const [codeGroupList, setCodeGroupList] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingData, setEditingData] = useState({});

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    setCodeGroupInput("");
    setSelectedCodeGroup("");
    setFilterValue("");
    setCodeGroupList([]);
    setSelectedCodeGroupDetails([]);
    setIsEditMode(false);
    await getItemSample();
  };

  const getItemSample = async () => {
    try {
      const res = await fetch("/request/getCommonCodeGroup", {
        credentials: "include",
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCodeGroupList(data);
      }
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdDtl = async (codeGroup) => {
    try {
      const res = await fetch(`/request/getStdDtl?std_group_code=${codeGroup}`, {
        credentials: "include"
      });
      const data = await res.json();
      setSelectedCodeGroupDetails(data);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const insertStdInfo = async (codeGroupInput) => {
    if (!codeGroupInput.trim()) return;
    try {
      const res = await fetch("/request/InsertStdInfo", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codeGroupInput }),
      });
      await res.json();
      alert(`새 코드그룹 [${codeGroupInput}] 생성`);
      setCodeGroupInput("");
      await getItemSample();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const deleteStdInfo = async (codeGroup) => {
    if (!codeGroup) {
      alert("삭제할 코드그룹을 선택해주세요.");
      return;
    }
    if (!window.confirm(`${codeGroup}을 정말 삭제하시겠습니까?`)) return;

    try {
      const res = await fetch("/request/deleteStdInfo", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_code: codeGroup }),
      });
      if (!res.ok) throw new Error(`Server responded with status code ${res.status}`);

      alert("삭제 되었습니다.");
      setSelectedCodeGroupDetails([]);
      setSelectedCodeGroup("");
      await getItemSample();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleCodeGroupClick = (codeGroup) => {
    setIsEditMode(false);
    setSelectedRows(new Set());
    getStdDtl(codeGroup);
    setSelectedCodeGroup(codeGroup);
  };

  const getFilteredRows = () => {
    const filterText = filterValue.toLowerCase();
    return codeGroupList.filter((row) =>
      row.std_group_code.toLowerCase().includes(filterText)
    );
  };

  const handleRowSelect = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const handleEdit = () => {
    if (selectedRows.size === 0) {
      alert("수정할 항목을 선택해주세요.");
      return;
    }
    setIsEditMode(true);
  };

  const handleSave = async () => {
    try {
      for (const index of selectedRows) {
        const item = selectedCodeGroupDetails[index];
        const editData = editingData[index] || {};

        await fetch("/request/UpdateStdInfo", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            group_code: editData.std_group_code || item.std_group_code,
            key: editData.std_key || item.std_key,
            value: editData.std_value || item.std_value,
            description: editData.std_description || item.std_description,
            id: item.std_id,
          }),
        });
      }

      alert("적용되었습니다.");
      setIsEditMode(false);
      setSelectedRows(new Set());
      setEditingData({});
      getStdDtl(selectedCodeGroup);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleAddNew = () => {
    const newItem = {
      std_id: '',
      std_group_code: selectedCodeGroup,
      std_key: '',
      std_value: '',
      std_description: '',
    };

    const newIndex = selectedCodeGroupDetails.length;
    setSelectedCodeGroupDetails([...selectedCodeGroupDetails, newItem]);
    setSelectedRows(new Set([newIndex]));
    setIsEditMode(true);
  };

  const handleDelete = async (std_id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const res = await fetch("/request/deleteStdInfoCheck", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ std_id }),
      });

      if (!res.ok) throw new Error(`Failed to delete std_id: ${std_id}`);

      alert("삭제 되었습니다.");
      getStdDtl(selectedCodeGroup);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleInputChange = (index, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const filteredGroups = getFilteredRows();

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          eyebrow="기준정보"
          title="기준정보 관리"
          subtitle="코드그룹과 상세 코드를 등록하고 관리합니다."
          actions={
            <Button
              $variant="primary"
              onClick={() => insertStdInfo(codeGroupInput)}
            >
              <FiPlus /> 코드그룹 추가
            </Button>
          }
        />

        <Grid>
          {/* 좌측: 코드그룹 리스트 */}
          <Panel $pad={false}>
            <CardHeader>
              <CardTitle>
                <FiLayers /> 코드그룹
              </CardTitle>
              <Badge $tone="neutral">{filteredGroups.length}</Badge>
            </CardHeader>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <SearchInput
                placeholder="찾을 코드를 입력해주세요."
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                grow="0 0 auto"
              />

              <GroupScroll>
                {filteredGroups.length === 0 ? (
                  <EmptyState
                    icon={<FiFolder />}
                    title="코드그룹이 없습니다"
                    description="아래에서 새 코드그룹을 추가해 보세요."
                    pad="32px 16px"
                  />
                ) : (
                  <SideList>
                    {filteredGroups.map((row, index) => (
                      <SideItem
                        key={index}
                        $active={selectedCodeGroup === row.std_group_code}
                        onClick={() => handleCodeGroupClick(row.std_group_code)}
                      >
                        <span>{row.std_group_code}</span>
                      </SideItem>
                    ))}
                  </SideList>
                )}
              </GroupScroll>

              <AddArea>
                <AddRow>
                  <SearchInput
                    placeholder="새 코드그룹명을 입력하세요."
                    value={codeGroupInput}
                    onChange={(e) => setCodeGroupInput(e.target.value)}
                    onSearch={() => insertStdInfo(codeGroupInput)}
                    grow="0 0 auto"
                  />
                </AddRow>
                <AddRow style={{ justifyContent: "space-between" }}>
                  <IconBtn
                    $size="sm"
                    title="선택한 코드그룹 삭제"
                    onClick={() => deleteStdInfo(selectedCodeGroup)}
                  >
                    <FiTrash2 />
                  </IconBtn>
                  <Button
                    $variant="primary"
                    $size="sm"
                    onClick={() => insertStdInfo(codeGroupInput)}
                  >
                    <FiPlus /> 추가
                  </Button>
                </AddRow>
              </AddArea>
            </div>
          </Panel>

          {/* 우측: 상세 내용 */}
          <Panel $pad={false}>
            <CardHeader>
              <CardTitle>
                <FiList /> 상세 내용
                {selectedCodeGroup ? (
                  <Badge $tone="brand" $dot>{selectedCodeGroup}</Badge>
                ) : null}
              </CardTitle>
            </CardHeader>

            {!selectedCodeGroup ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EmptyState
                  icon={<FiInbox />}
                  title="코드그룹을 선택해주세요"
                  description="좌측에서 코드그룹을 선택하면 상세 코드를 확인하고 편집할 수 있습니다."
                />
              </div>
            ) : (
              <>
                <TableCard style={{ border: "none", boxShadow: "none", borderRadius: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <TableScroll style={{ flex: 1, minHeight: 0 }}>
                    <Table>
                      <thead>
                        <tr>
                          <Th style={{ width: 56 }} $align="center">선택</Th>
                          <Th>그룹코드</Th>
                          <Th>키</Th>
                          <Th>값</Th>
                          <Th>설명</Th>
                          <Th style={{ width: 80 }} $align="center">삭제</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCodeGroupDetails.length === 0 ? (
                          <tr>
                            <td colSpan={6}>
                              <EmptyState
                                icon={<FiInbox />}
                                title="등록된 상세 코드가 없습니다"
                                description="하단의 추가 버튼으로 새 상세 코드를 등록할 수 있습니다."
                                pad="40px 16px"
                              />
                            </td>
                          </tr>
                        ) : (
                          selectedCodeGroupDetails.map((item, index) => (
                            <Tr key={index} $selected={selectedRows.has(index)}>
                              <Td $align="center">
                                <Checkbox
                                  checked={selectedRows.has(index)}
                                  onChange={() => handleRowSelect(index)}
                                />
                              </Td>
                              <Td>
                                {isEditMode && selectedRows.has(index) ? (
                                  <CellInput
                                    value={editingData[index]?.std_group_code || item.std_group_code}
                                    onChange={(e) => handleInputChange(index, 'std_group_code', e.target.value)}
                                  />
                                ) : (
                                  item.std_group_code
                                )}
                              </Td>
                              <Td>
                                {isEditMode && selectedRows.has(index) ? (
                                  <CellInput
                                    value={editingData[index]?.std_key || item.std_key}
                                    onChange={(e) => handleInputChange(index, 'std_key', e.target.value)}
                                  />
                                ) : (
                                  item.std_key
                                )}
                              </Td>
                              <Td>
                                {isEditMode && selectedRows.has(index) ? (
                                  <CellInput
                                    value={editingData[index]?.std_value || item.std_value}
                                    onChange={(e) => handleInputChange(index, 'std_value', e.target.value)}
                                  />
                                ) : (
                                  item.std_value
                                )}
                              </Td>
                              <Td $muted>
                                {isEditMode && selectedRows.has(index) ? (
                                  <CellInput
                                    value={editingData[index]?.std_description || item.std_description}
                                    onChange={(e) => handleInputChange(index, 'std_description', e.target.value)}
                                  />
                                ) : (
                                  item.std_description
                                )}
                              </Td>
                              <Td $align="center">
                                <IconBtn
                                  $size="sm"
                                  title="삭제"
                                  onClick={() => handleDelete(item.std_id)}
                                >
                                  <FiTrash2 />
                                </IconBtn>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </TableScroll>
                </TableCard>

                <Footer>
                  {isEditMode ? (
                    <Button $variant="primary" onClick={handleSave}>
                      <FiSave /> 저장
                    </Button>
                  ) : (
                    <>
                      <Button $variant="secondary" onClick={handleEdit}>
                        <FiEdit2 /> 수정
                      </Button>
                      <Button $variant="primary" onClick={handleAddNew}>
                        <FiPlus /> 추가
                      </Button>
                    </>
                  )}
                </Footer>
              </>
            )}
          </Panel>
        </Grid>
      </PageWidth>
    </PageShell>
  );
}
